import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient, updateClient, deleteClient, setClientActivo } from '../services/apiService';
import { FaEdit, FaTrashAlt, FaChevronLeft, FaChevronRight, FaBan, FaCheckCircle, FaSearch } from 'react-icons/fa';

const ClientesPage = () => {
  const navigate = useNavigate();
  // 1. Definición de todos los estados (Esto corrige los errores de 'no-undef')
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', email: '', telefono: '', direccion: '' });
  const [search, setSearch] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClients(currentPage, 10, search);
      if (data && data.clients) {
        setClientes(data.clients);
        setTotalPages(data.totalPages || 1);
        setTotalCount(data.totalCount || 0);
      } else {
        // Por si el backend aún devuelve el formato antiguo (array plano)
        setClientes(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al cargar clientes:', err);
      setError(err.message || 'No se pudieron cargar los clientes.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  // 2. Cargar clientes al iniciar, al cambiar de página, y (con un pequeño
  //    debounce) al buscar.
  useEffect(() => {
    const timer = setTimeout(() => fetchClients(), search ? 350 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // cualquier búsqueda nueva vuelve a la página 1
  };

  // 3. Auto-ocultar mensajes de éxito (Mejora de UX)
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setMessage(''); 
    setError('');
    try {
      if (editingClient) { 
        await updateClient(editingClient.id, formData); 
        setMessage('✅ Cliente actualizado con éxito.'); 
      } else { 
        await createClient(formData); 
        setMessage('🚀 Cliente creado con éxito.'); 
      }
      setFormData({ nombre: '', email: '', telefono: '', direccion: '' });
      setEditingClient(null);
      fetchClients();
    } catch (err) { 
      setError(err.message || 'Error al guardar el cliente.'); 
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({ nombre: client.nombre, email: client.email || '', telefono: client.telefono || '', direccion: client.direccion || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) return;
    try { 
      await deleteClient(clientId); 
      setMessage('🗑️ Cliente eliminado.'); 
      fetchClients(); 
    } catch (err) { 
      setError(err.message || 'Error al eliminar el cliente.'); 
    }
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
    setFormData({ nombre: '', email: '', telefono: '', direccion: '' });
  };

  const handleToggleActivo = async (client) => {
    const confirmMsg = client.activo
      ? `¿Desactivar a "${client.nombre}"? Ya no aparecerá como opción para nuevas ventas.`
      : `¿Reactivar a "${client.nombre}"?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await setClientActivo(client.id, !client.activo);
      setMessage(`✅ ${client.nombre} fue ${client.activo ? 'desactivado' : 'reactivado'}.`);
      fetchClients();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado del cliente.');
    }
  };

  if (loading) return <div className="p-10 text-center text-emerald-600 font-bold">Cargando clientes...</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-8 animate-fadeIn">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl md:text-3xl font-black text-gray-800">
          Gestión de <span className="text-emerald-500">Clientes</span>
        </h2>
      </div>

      {/* Formulario */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-bold mb-4 text-gray-700">
          {editingClient ? '📝 Editar Cliente' : '👤 Nuevo Cliente'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" name="nombre" placeholder="Nombre completo" value={formData.nombre} onChange={handleChange} required className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500 outline-none" />
            <input type="email" name="email" placeholder="Correo electrónico" value={formData.email} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500 outline-none" />
            <input type="tel" name="telefono" placeholder="Celular (ej. 3001234567)" value={formData.telefono} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500 outline-none" />
            <input type="text" name="direccion" placeholder="Dirección" value={formData.direccion} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500 outline-none" />
          </div>

          <div className="flex gap-3">
            <button type="submit" className="flex-1 bg-emerald-500 text-white font-bold py-2 rounded-xl hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20">
              {editingClient ? 'Actualizar' : 'Guardar Cliente'}
            </button>
            {editingClient && <button type="button" onClick={handleCancelEdit} className="flex-1 bg-gray-200 text-gray-600 font-bold py-2 rounded-xl">Cancelar</button>}
          </div>
        </form>
        {message && <div className="mt-4 p-3 bg-emerald-50 text-emerald-700 rounded-xl text-center font-medium animate-pulse">{message}</div>}
        {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-xl text-center font-medium">{error}</div>}
      </div>

      {/* Tabla / Lista */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <h3 className="font-bold text-gray-700">Lista de Clientes</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={12} />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Buscar por nombre o celular..."
                className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500 w-64"
              />
            </div>
            <span className="text-sm text-gray-400 whitespace-nowrap">{totalCount} registrados</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-bold text-gray-500 uppercase tracking-widest">
                <th className="px-6 py-4">Nombre</th>
                <th className="px-6 py-4">Contacto</th>
                <th className="px-6 py-4 text-center">Estado</th>
                <th className="px-6 py-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {clientes.map(client => (
                <tr
                  key={client.id}
                  onClick={() => navigate(`/clientes/${client.id}`)}
                  className={`hover:bg-blue-50/30 transition-colors cursor-pointer ${!client.activo ? 'opacity-60' : ''}`}
                >
                  <td className="px-6 py-4 font-medium text-gray-800">{client.nombre}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{client.email || 'Sin correo'}<br/>{client.telefono}</td>
                  <td className="px-6 py-4 text-center">
                    {client.activo ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-700">
                        <FaCheckCircle /> Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-red-100 text-red-600">
                        <FaBan /> Inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(client); }} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Editar"><FaEdit /></button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleActivo(client); }}
                        title={client.activo ? 'Desactivar' : 'Reactivar'}
                        className={`p-2 rounded-lg ${client.activo ? 'text-amber-500 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                      >
                        {client.activo ? <FaBan /> : <FaCheckCircle />}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(client.id); }} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Eliminar"><FaTrashAlt /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* --- CONTROLES DE PAGINACIÓN --- */}
        <div className="bg-gray-50/50 px-6 py-4 flex items-center justify-between border-t border-gray-100">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">
            Página <span className="text-emerald-600">{currentPage}</span> de {totalPages}
          </p>

          <div className="flex gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-black text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <FaChevronLeft /> Anterior
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black hover:bg-emerald-600 shadow-lg shadow-emerald-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Siguiente <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ESTA LÍNEA ES LA MÁS IMPORTANTE (Corrige el error de App.js)
export default ClientesPage;